// src/map/plateOverlay/resolveCorners.ts
//
// Georef resolution for PlateOverlayLayer.
//
// Corner resolution priority (for each plate spec):
//   1. /api/plates/georef  — text-layer affine georef (most accurate, ~0.2 NM)
//   2. spec.corners        — pre-computed corners passed with the spec
//   3. /api/procedures     — CIFP bounding-box fallback (~1–2 NM)

import {
  type PlateOverlaySpec,
  type PlateBboxCorners,
  computeCourseAlignedCorners,
  validatePlateCorners,
} from '../../lib/plateGeoref';

// ── Local types ─────────────────────────────────────────────────────────────

export interface GeorefResponse {
  pdfName: string;
  cycle: string;
  corners: PlateBboxCorners | null;
  /**
   * Full-page geographic corners (no plan-view crop). Lets the client render
   * the entire chart including the top header / minima / profile sections.
   */
  pageCorners?: PlateBboxCorners | null;
  planViewRect: PlateRect | null;
  planViewFraction: number;
  courseDegTrue: number | null;
  residualNm: number | null;
  /** Debug telemetry from server: matched fix pairs used for the affine. */
  matches?: Array<{
    name: string;
    px: number; py: number;
    pxNorm: number; pyNorm: number;
    lat: number; lon: number;
  }>;
  pageSize?: { w: number; h: number };
  status: 'ok' | 'low-confidence' | 'failed' | 'not-found';
  /**
   * Server pipeline tag: 'batch' for IAP corpus, 'batch-star' for STAR
   * sibling blob, 'manual_correction' for user-corrected overrides.
   */
  source?: 'batch' | 'batch-star' | 'manual_correction';
}

export interface PlateRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

// ── CIFP procedure leg (minimal) ────────────────────────────────────────────

interface CifpLeg {
  legType?: string;
  fixIdent: string;
  fixLat: number | null;
  fixLon: number | null;
  isMissedApproach: boolean;
  isHoldingPattern?: boolean;
  holdInboundCourse?: number | null;
  turnDirection?: 'L' | 'R' | null;
  holdLeg?:
    | { kind: 'time'; minutes: number }
    | { kind: 'distance'; nm: number }
    | null;
  altitude1?: string | null;
}

interface CifpProcedure {
  routeType: string;
  procIdent?: string;
  transitions: Array<{ legs: CifpLeg[] }>;
}

// ── Resolved match type ──────────────────────────────────────────────────────

export interface ResolvedMatch {
  name: string;
  lat: number;
  lon: number;
}

// ── Resolved corners result ──────────────────────────────────────────────────

export interface ResolvedCorners {
  corners: PlateBboxCorners;
  /** Full-page corners when the server provides them (server georef only). */
  pageCorners: PlateBboxCorners | null;
  planViewFraction: number;
  planViewRect: PlateRect | null;
  courseDegTrue?: number;
  matches?: ResolvedMatch[];
  /** Status of the resolution: matches uiStore.plateOverlayResult.status. */
  resultStatus: 'ok' | 'low-confidence' | 'fallback-cifp' | 'spec-corners';
  /** Server-reported affine residual in NM (null when not from server). */
  residualNm: number | null;
}

// ── Main resolution function ─────────────────────────────────────────────────

/**
 * Resolve the MapLibre corners for a plate overlay.
 *
 * 1. Try /api/plates/georef (text-layer affine, best accuracy).
 * 2. If spec has pre-computed corners, use them.
 * 3. Fetch CIFP and compute course-aligned bounding box.
 */
export async function resolveCorners(
  spec: PlateOverlaySpec,
  signal: AbortSignal,
  /** Phase 3: bearer token from the signed-in user. When present, the georef
   *  endpoint also returns pending corrections the user submitted themselves,
   *  so they can preview their own work before cartographer approval. */
  userToken?: string,
): Promise<ResolvedCorners> {
  // 1. Try text-layer georef endpoint. Accept both 'ok' AND 'low-confidence'
  // corners — a low-confidence v14 fit (residual 0.1–5 NM) is still much
  // closer than the CIFP-bbox fallback would be. The Controls component
  // renders a cockpit-awareness banner so the pilot knows not to trust it
  // blindly.
  try {
    const georefUrl = `/api/plates/georef?cycle=${encodeURIComponent(spec.cycle)}&pdfName=${encodeURIComponent(spec.pdfName)}`;
    const georefHeaders: HeadersInit = {};
    // Phase 3: attach bearer token so authenticated users can see their own
    // pending corrections on the chart before cartographer approval.
    if (userToken) georefHeaders['Authorization'] = `Bearer ${userToken}`;
    const resp = await fetch(georefUrl, { signal, cache: userToken ? 'no-store' : 'default', headers: georefHeaders });
    if (!signal.aborted && resp.ok) {
      const data = (await resp.json()) as GeorefResponse;
      if ((data.status === 'ok' || data.status === 'low-confidence') && data.corners) {
        // Backstop: reject pathological georefs (mirrored / flipped / wrong
        // location / impossible span / squished aspect ratio). Catches the
        // 'status:ok with residual=0.0 from a 2-anchor degenerate fit' class
        // of false positives.
        let pageAspect: number | undefined;
        if (data.pageSize && data.planViewRect) {
          const pvWidthPt =
            (data.planViewRect.maxX - data.planViewRect.minX) * data.pageSize.w;
          const pvHeightPt =
            (data.planViewRect.maxY - data.planViewRect.minY) * data.pageSize.h;
          if (pvWidthPt > 0 && pvHeightPt > 0) {
            pageAspect = pvWidthPt / pvHeightPt;
          }
        }
        // STARs cover wide transition corridors — a KJFK PUCKY plate
        // legitimately spans CT/MA-into-NY (~180–220 NM). Bump the
        // max-span tolerance from the IAP default (80 NM) to 300 NM for
        // STAR server responses.
        const isStar = data.source === 'batch-star';
        const invalid = validatePlateCorners(data.corners, {
          airportLat: spec.airportLat,
          airportLon: spec.airportLon,
          pdfName: spec.pdfName,
          pageAspect,
          maxSpanNm: isStar ? 300 : 80,
        });
        if (invalid) {
          console.warn(
            `[PlateOverlay] rejected server georef for ${spec.pdfName} (${spec.airportIdent ?? '?'}): ${invalid}`,
          );
          // Fall through to spec corners / CIFP-bbox fallback.
        } else {
          return {
            corners: data.corners,
            pageCorners: data.pageCorners ?? null,
            planViewFraction: data.planViewFraction ?? spec.planViewFraction ?? 0.62,
            planViewRect: data.planViewRect ?? null,
            courseDegTrue: data.courseDegTrue ?? spec.courseDegTrue,
            matches: data.matches?.map((m) => ({ name: m.name, lat: m.lat, lon: m.lon })),
            resultStatus: data.status,
            residualNm: data.residualNm,
          };
        }
      }
    }
  } catch {
    // Network error or abort — fall through to next option
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  }

  // 2. Use pre-computed corners from spec
  if (spec.corners) {
    return {
      corners: spec.corners,
      pageCorners: null,
      planViewFraction: spec.planViewFraction ?? 0.62,
      planViewRect: null,
      courseDegTrue: spec.courseDegTrue,
      resultStatus: 'spec-corners',
      residualNm: null,
    };
  }

  // 3. Fetch CIFP procedures and compute course-aligned bounding box
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  try {
    const cifpUrl = `/api/procedures?airport=${encodeURIComponent(spec.airportIdent)}`;
    const cifpResp = await fetch(cifpUrl, { signal, cache: 'default' });
    if (cifpResp.ok && !signal.aborted) {
      const data = (await cifpResp.json()) as { procedures: CifpProcedure[] };
      const fixes: Array<{ lat: number; lon: number }> = [];
      const seen = new Set<string>();
      for (const proc of data.procedures || []) {
        if (proc.routeType !== 'IAP') continue;
        for (const t of proc.transitions) {
          for (const leg of t.legs) {
            if (leg.isMissedApproach || leg.fixLat == null || leg.fixLon == null) continue;
            const key = `${leg.fixLat.toFixed(5)},${leg.fixLon.toFixed(5)}`;
            if (!seen.has(key)) {
              seen.add(key);
              fixes.push({ lat: leg.fixLat, lon: leg.fixLon });
            }
          }
        }
      }
      if (fixes.length >= 2) {
        const result = computeCourseAlignedCorners(fixes, 2, 0.85);
        return {
          corners: result.corners,
          pageCorners: null,
          planViewRect: null,
          planViewFraction: spec.planViewFraction ?? 0.62,
          courseDegTrue: spec.courseDegTrue ?? result.courseDegTrue,
          resultStatus: 'fallback-cifp',
          residualNm: null,
        };
      }
    }
  } catch {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  }

  throw new Error(`PlateOverlay: cannot resolve corners for ${spec.pdfName}`);
}
